import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, RotateCw, Ban, Loader2 } from "lucide-react";

const MODES = [
  { value: "lien", label: "Lien sécurisé (72 h) — la personne choisit son mot de passe" },
  { value: "mot_de_passe_temporaire", label: "Mot de passe temporaire par e-mail — à changer à la 1re connexion" },
];
const STATUT = { envoyee: ["Envoyée", "bg-amber-100 text-amber-800"], acceptee: ["Acceptée", "bg-emerald-100 text-emerald-800"],
  expiree: ["Expirée", "bg-slate-200 text-slate-700"], revoquee: ["Révoquée", "bg-red-100 text-red-700"] };

const emailToast = (r, ok) => r.email_statut === "envoye" ? toast.success(ok) : toast.warning("Enregistré, mais l'e-mail n'est pas parti : utilisez « Renvoyer ».");

function ModeSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function InviteDialog({ open, onClose, roles, territoires, entreprises, onDone }) {
  const [f, setF] = useState({ email: "", name: "", role: "gestionnaire_ge", mode: "lien", territoire: "", entreprise_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const submit = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data } = await api.post("/auth/invitations", body);
      emailToast(data, `Invitation envoyée à ${data.email}`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Inviter une personne</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>E-mail</Label><Input value={f.email} onChange={set("email")} data-testid="invite-email" /></div>
          <div className="space-y-1.5"><Label>Nom affiché</Label><Input value={f.name} onChange={set("name")} data-testid="invite-name" /></div>
          <div className="space-y-1.5"><Label>Rôle</Label>
            <Select value={f.role} onValueChange={set("role")}><SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Territoire</Label>
            <Select value={f.territoire} onValueChange={set("territoire")}><SelectTrigger data-testid="invite-territoire"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{territoires.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          {f.role === "employeur" && <div className="space-y-1.5 md:col-span-2"><Label>Entreprise</Label>
            <Select value={f.entreprise_id} onValueChange={set("entreprise_id")}><SelectTrigger data-testid="invite-entreprise"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{entreprises.map((e) => <SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent></Select></div>}
          <div className="space-y-1.5 md:col-span-2"><Label>Mode d'accès</Label><ModeSelect value={f.mode} onChange={set("mode")} testid="invite-mode" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !f.email || !f.name} data-testid="invite-submit" className="bg-[#1B2A4A] text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}Envoyer l'invitation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetLinkDialog({ target, onClose, onDone }) {
  const [mode, setMode] = useState("lien");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/users/${target.id}/lien-reinitialisation`, { mode });
      emailToast(data, `Envoyé à ${target.email}`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reset-link-dialog">
        <DialogHeader><DialogTitle>Réinitialiser le mot de passe</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Compte : <span className="font-mono">{target?.email}</span>. Les liens précédents sont révoqués.</p>
        <ModeSelect value={mode} onChange={setMode} testid="reset-link-mode" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="reset-link-submit" className="bg-[#1B2A4A] text-white">Envoyer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InvitationsCard({ refreshKey }) {
  const [data, setData] = useState({ items: [], counts: {} });
  const load = useCallback(() => api.get("/auth/invitations").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const act = async (inv, verb) => {
    try {
      const { data: r } = await api.post(`/auth/invitations/${inv.id}/${verb}`);
      verb === "renvoyer" ? emailToast(r, "Invitation renvoyée") : toast.success("Invitation révoquée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <Card data-testid="invitations-card">
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Invitations et liens envoyés ({data.items.length})</CardTitle></CardHeader>
      <CardContent>
        {data.items.length === 0 ? <p className="text-sm text-slate-500">Aucune invitation.</p> : (
          <div className="divide-y">
            {data.items.slice(0, 50).map((i) => (
              <div key={i.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`invitation-row-${i.id}`}>
                <div className="min-w-0 text-sm">
                  <span className="font-mono text-xs">{i.email}</span>
                  <span className="text-xs text-slate-500 ml-2">{i.type === "reinitialisation" ? "Réinitialisation" : "Invitation"} · {i.mode === "lien" ? "lien 72 h" : "mot de passe temporaire"} · {i.envois} envoi(s){i.email_statut === "echec" && <span className="text-red-600"> · e-mail en échec</span>}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={STATUT[i.statut]?.[1]} data-testid={`invitation-statut-${i.id}`}>{STATUT[i.statut]?.[0] || i.statut}</Badge>
                  {["envoyee", "expiree"].includes(i.statut) && <>
                    <Button size="sm" variant="outline" onClick={() => act(i, "renvoyer")} data-testid={`invitation-renvoyer-${i.id}`}><RotateCw className="h-3 w-3 mr-1" />Renvoyer</Button>
                    <Button size="sm" variant="outline" onClick={() => act(i, "revoquer")} data-testid={`invitation-revoquer-${i.id}`}><Ban className="h-3 w-3 mr-1" />Révoquer</Button>
                  </>}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
